import type { MaybeRef } from '@vueuse/core'

import merge from 'lodash-es/merge'
import { useField, FieldContext as VValidateFieldContext } from 'vee-validate'
import { inject, InjectionKey, provide, ref, unref } from 'vue'

export type FieldContext = ReturnType<typeof createFieldContext>
export const useFieldSymbolContext = Symbol() as InjectionKey<FieldContext>

let fieldId = 0

function createFieldContext<TValue = unknown>(id?: MaybeRef<string | undefined>) {
  const newId = unref(id)
  fieldId += 1
  const $id = ref(id ?? `v-field-${fieldId}`)
  const field = ref<VValidateFieldContext<TValue>>()

  if (newId) {
    field.value = useField(newId)
  }

  const vFieldContext = {
    id: $id,
    field,
  }

  provide(useFieldSymbolContext, vFieldContext)

  return vFieldContext
}

interface FieldContextOption {
  id?: MaybeRef<string | undefined>
  create?: MaybeRef<boolean>
  inherit?: MaybeRef<boolean>
  help?: MaybeRef<string>
}

export function useFieldContext(options = {} as FieldContextOption) {
  const newOptions = merge(
    {},
    {
      create: true,
      inherit: true,
    },
    options,
  )

  if (unref(newOptions.inherit)) {
    const vFieldContext = inject(useFieldSymbolContext, undefined)
    if (vFieldContext) {
      return vFieldContext
    }
  }

  const help = unref(newOptions.help) ? unref(newOptions.help) + ': ' : ''

  if (!unref(newOptions.create)) {
    throw new Error(
      `${help}useFieldContext (create = false) must be used inside a Field component`,
    )
  }

  return createFieldContext(newOptions.id)
}
